import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { format, parseISO } from 'date-fns'
import LayoutSidebar from '../../../components/LayoutSidebar'
import Loading from '../../../components/Loading'
import { useRequest } from '../../../hooks/UseRequest'
import { selectAuth } from '../../../redux/authSlice'
import { selectUser } from '../../../redux/userSlice'
import { selectActivePermissions } from '../../../redux/permissionSelectors'
import { setNotification } from '../../../redux/notificationSlice'
import { hasUserPermission } from '../../../utils/permissionHelper'
import ProviderSplitTable from './components/ProviderSplitTable'
import ProviderSplitFooter from './components/ProviderSplitFooter'
import ConfirmProviderSplitModal from './components/ConfirmProviderSplitModal'
import {
  buildChangeRows,
  buildFormValues,
  buildPayload,
  findInvalidCells,
  formatInvalidCells,
  isVerificationTypeEditable,
} from './providerSplitHelpers'

const ENDPOINT = `${process.env.REACT_APP_API_DOMAIN}/bank_verification_provider_split`

export default function BankVerificationProviderSplit() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const request = useRequest()
  const auth = useSelector(selectAuth)
  const user = useSelector(selectUser)
  const [grid, setGrid] = useState(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const requesting = useRef(false)
  const { register, reset, watch } = useForm({ defaultValues: {} })
  const values = watch()

  const permissions = useSelector(selectActivePermissions)
  const canView = hasUserPermission(
    permissions,
    'bank_verification_provider_split_read'
  )
  const canUpdate = hasUserPermission(
    permissions,
    'bank_verification_provider_split_update'
  )
  const permissionsLoaded = user.status === 'succeeded'

  const originalValues = useMemo(
    () => (grid ? buildFormValues(grid) : {}),
    [grid]
  )
  const invalidCells = grid && canUpdate ? findInvalidCells(grid, values) : []
  const changes =
    grid && canUpdate ? buildChangeRows(grid, originalValues, values) : []

  const applyGrid = (data) => {
    setGrid(data)
    reset(buildFormValues(data))
  }

  const fetchGrid = async () => {
    if (requesting.current) return
    requesting.current = true
    setLoading(true)
    try {
      request.setEndpoint(ENDPOINT)
      const response = await request.get()
      applyGrid(response.data.data)
    } catch (error) {
      dispatch(
        setNotification({
          type: 'error',
          body:
            error.response?.data?.message ??
            'Unable to load the provider split.',
        })
      )
    } finally {
      requesting.current = false
      setLoading(false)
    }
  }

  useEffect(() => {
    if (permissionsLoaded && !canView) navigate('/403')
  }, [permissionsLoaded, canView])

  // Load once: a silent token refresh or user-cache refresh re-runs this effect, and refetching would reset() away unsaved edits.
  useEffect(() => {
    if (!auth.fromPageChange || grid) return
    if (auth.accessToken && permissionsLoaded && canView) fetchGrid()
  }, [auth.accessToken, permissionsLoaded, canView])

  const onConfirm = async (reason) => {
    setSaving(true)
    let savedGrid
    try {
      request.setEndpoint(ENDPOINT)
      const trimmedReason = reason.trim()
      const response = await request.edit({
        splits: buildPayload(grid, originalValues, values),
        reason: trimmedReason === '' ? null : trimmedReason,
      })
      if (response.status !== 200) throw new Error(`Error: ${response.status}`)
      savedGrid = response.data?.data
    } catch (error) {
      dispatch(
        setNotification({
          type: 'error',
          body: error.response?.data?.message ?? error.message,
        })
      )
      return
    } finally {
      setSaving(false)
    }

    setIsConfirmOpen(false)
    dispatch(
      setNotification({
        type: 'success',
        body: (
          <>
            <strong>Provider split saved.</strong> New verifications will use
            the updated %.
          </>
        ),
      })
    )
    // The save already succeeded, so an unexpected response shape reloads the grid instead of reporting a failure.
    if (savedGrid?.splits) applyGrid(savedGrid)
    else fetchGrid()
  }

  const content = (
    <div
      className="lox-provider-split"
      data-testid="bank-verification-provider-split"
    >
      <div className="lox-provider-split__copy mb-4">
        <p>
          Set what % of bank verifications are routed to each provider, per
          portfolio. Manual Verification and SSV are configured independently.
        </p>
        {grid?.last_updated && (
          <p
            className="has-text-right has-text-grey"
            data-testid="bank-verification-provider-split-last-updated"
          >
            Last updated{' '}
            {format(
              parseISO(grid.last_updated.updated_at),
              'MMM d, yyyy h:mm a'
            )}
            {grid.last_updated.updated_by_name
              ? ` by ${grid.last_updated.updated_by_name}`
              : ''}
          </p>
        )}
      </div>

      {grid && !canUpdate && (
        <div
          className="notification is-light"
          data-testid="bank-verification-provider-split-view-only"
        >
          <strong>View only.</strong> You can see the current split but
          don&apos;t have permission to change it. Ask an admin for{' '}
          <code>bank_verification_provider_split_update</code>.
        </div>
      )}

      {invalidCells.length > 0 && (
        <div
          className="notification is-danger is-light"
          data-testid="bank-verification-provider-split-error-banner"
        >
          <strong>Can&apos;t save yet.</strong> Each portfolio must total
          exactly 100%. Fix: {formatInvalidCells(invalidCells)}.
        </div>
      )}

      {loading && <Loading />}

      {grid &&
        grid.verification_types.map((verificationType) => (
          <ProviderSplitTable
            key={verificationType.id}
            verificationType={verificationType}
            providers={grid.providers}
            portfolios={grid.portfolios}
            values={values}
            originalValues={originalValues}
            canEdit={canUpdate && isVerificationTypeEditable(verificationType)}
            readOnly={saving}
            register={register}
          />
        ))}

      {grid && (
        <p className="lox-provider-split__copy mb-4">
          Whole numbers 0 to 100. Set a provider to 0% to stop routing that
          portfolio to it.
          {canUpdate && (
            <>
              {' '}
              <span className="lox-provider-split__edited-dot" /> Edited, not
              saved
            </>
          )}
        </p>
      )}

      {grid && canUpdate && (
        <ProviderSplitFooter
          changedCount={changes.length}
          hasErrors={invalidCells.length > 0}
          onCancel={() => reset(originalValues)}
          onSave={() => setIsConfirmOpen(true)}
        />
      )}

      {isConfirmOpen && (
        <ConfirmProviderSplitModal
          changes={changes}
          saving={saving}
          onConfirm={onConfirm}
          onClose={() => setIsConfirmOpen(false)}
        />
      )}
    </div>
  )

  return (
    <LayoutSidebar
      contentTitle="Bank Verification Provider Split"
      content={content}
    />
  )
}
